<template>
  <div
    class="dataLevelTable"
    :class="{
      'dataLevelTable--has-horizontal-overflow': hasHorizontalOverflow,
      'dataLevelTable--reduced-row-height': reducedRowHeightEnabled
    }"
    :style="{ '--data-level-table-max-height': tableMaxHeight }"
  >
  <v-data-table
    class="ma-3"
    :headers="tableHeader"
    :items="tableData"
    :custom-sort="sortDataTableLevels"
    hide-default-footer
    :items-per-page="-1"
    fixed-header
    @click:row="tableRowClickFunction ? tableRowClickFunction($event) : null"
  >
    <!-- formate table headers -->
    <template v-slot:top>
        <v-dialog v-model="dialogDelete" max-width="550px">
          <v-card>
            <v-card-title class="dialog_title"> Delete Item </v-card-title>
            <v-card-text class="dialog_content">
              <div>
                Are you sure you want to delete this row?
              </div>
            </v-card-text>
            <v-card-actions>
              <v-row class="ma-0 pa-0">
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-btn class="action_text" outlined color="black" @click="dialogDelete = false;">Cancel</v-btn>
                </v-col>
                <v-col cols="auto">
                  <v-btn class="action_text" outlined color="black" @click="deleteItemConfirm">Delete</v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-dialog> 
    </template>
    <template v-for="h in tableHeader" v-slot:[`header.${h.value}`]>
      <v-tooltip :key="h.value" bottom>
        <template v-slot:activator="{ on }">
          <div
            v-on="on"
            :class="
              numericColumns.includes(h.value) ||
              decimalColumns.includes(h.value) || 
              currencyColumns.includes(h.value) ||
              percentageColumns.includes(h.value)
                ? 'headerCellFlighting'
                : 'headerCellFlighting headerStringCellFligthing'
            "
          >
            <v-menu v-model="filter_menus[h.value]" v-if="selectedLevels.includes(h.value) && filterable" :close-on-content-click="false" :close-on-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-0 mr-1" v-bind="attrs" v-on="on" :color="filter_vals[h.value].length == filter_all_vals[h.value].length ? 'grey' : 'black'">mdi-filter</v-icon>
              </template>
              <v-card class="filter_card">
                <v-card-title class="dialog_title"><v-icon class="mx-1" color="black">mdi-filter</v-icon>{{ h.text }}</v-card-title>
                <v-row class="ma-0 pa-0" style="max-height: 200px; overflow-y: scroll;">
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="filter_vals[h.value]"
                      :items="filter_all_vals[h.value]"
                      multiple
                    >
                    <template v-slot:prepend-item>
                        <v-list-item
                          ripple
                          @mousedown.prevent
                          @click="toggleDataLevelAllFilter(h.value)"
                        >
                          <v-list-item-action>
                            <v-icon>
                              {{ getDataLevelSelectAllIcon(h.value) }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>
                              Select All
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index === 0">
                          <span class="actionText">{{ item }}</span>
                        </v-chip>
                        <v-chip v-if="index === 1">
                          <span class="actionText">{{ item }}</span>
                        </v-chip>
                        <v-chip v-if="index === 2">
                          <span class="actionText">{{ item }}</span>
                        </v-chip>
                        <span
                          v-if="index === 3"
                          class="grey--text text-caption"
                        >
                          (+{{ filter_vals[h.value].length - 3 }} others)
                        </span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-card-actions>
                  <v-row class="ma-0 pa-0">
                    <v-spacer></v-spacer>
                    <v-col cols="auto">
                      <v-btn class="actionText" outlined @click="cancel_filter(h.value);$nextTick(() => { filter_menus[h.value] = false; })">Cancel</v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn class="actionText" outlined @click="save_filter(h.value); filter_menus[h.value] = false;" :disabled="arraysEqual(filter_vals[h.value], filter_base[h.value]) || filter_vals[h.value].length == 0">Save</v-btn>
                    </v-col>
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-menu>
            
            <span class="headerCellContent">
              {{ h.text }}
              <v-icon
                v-if="isEditableColumn(h.value)"
                small
                color="#6B7280"
                class="editableHeaderIndicator"
                title="Cells in this column can be edited directly"
              >
                mdi-tooltip-edit-outline
              </v-icon>
            </span>
          </div>
        </template>
        <TooltipDescription :title="h.text" :desc_2="h.desc"></TooltipDescription>
      </v-tooltip>
    </template>

    <!-- format all plain numeric columns -->
    <template v-for="numCol in tableHeader" v-slot:[`item.${numCol.value}`]="{ item, index}">
      <div
        v-if="numCol.value == 'location'"
        :key="numCol.value"
        :style="{ backgroundColor: update_color(item) }"
        class="locationCellFlighting ma-0 pa-1 px-2"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              small
              :disabled="!hasLocationCoordinates(item)"
              v-on="on"
              @click.stop="$emit('locationClick', item)"
            >
              <span
                class="locationMarker"
                :class="`locationMarker--${getLocationIconStyle(item).shape || 'circle'}`"
                :style="getLocationMarkerStyle(item)"
              ></span>
            </v-btn>
          </template>
          <TooltipDescription title="View Location" desc_2=""></TooltipDescription>
        </v-tooltip>
      </div>
      <div
        v-else-if="
          numericColumns.includes(numCol.value) ||
          decimalColumns.includes(numCol.value) ||
          currencyColumns.includes(numCol.value) ||
          percentageColumns.includes(numCol.value)
        "
        :key="numCol.value"
        :style="{ backgroundColor: update_color(item) }"
        class="numericCellFlighting ma-0 pa-1 px-2"
      >
        <div
          class="numeric-cell-content"
          :class="{ editableNumericCell: canEditCell(item, numCol.value) }"
          @click.stop="startEditCell(item, numCol.value)"
        >
          <v-text-field
            v-if="isEditingCell(item, numCol.value)"
            :value="inlineEditDraftValue"
            :error-messages="inlineEditError ? [inlineEditError] : []"
            :inputmode="getEditInputMode(numCol.value)"
            :step="getEditStep(numCol.value)"
            class="inlineNumericEditField"
            dense
            hide-details="auto"
            outlined
            single-line
            type="number"
            autofocus
            @blur="handleEditBlur(item, numCol.value)"
            @click.stop
            @input="updateEditValue"
            @keydown.enter.stop.prevent="handleEditEnter(item, numCol.value)"
            @keydown.esc.stop.prevent="handleEditEscape"
          ></v-text-field>
          <template v-else>
            <span :style="{color: update_text_color(item, numCol)}">
              {{ formatNumericCellValue(item[numCol.value], numCol.value) }}
            </span>
          </template>
        </div>
      </div>
      <div v-else-if="numCol.value == 'actions'"
        :key="numCol.value"
        :style="{ backgroundColor: update_color(item) }"
        class="actionCell"
      >
        <div class="actionCellContent">
          <v-tooltip top v-if="rowEditable && isCoreRow(item)">
              <template v-slot:activator="{ on }">
                  <v-btn icon elevation="0" v-on="on" @click="editOnClick(item)" :disabled="actions_locked">
                      <v-icon small color="black">mdi-pencil</v-icon>
                  </v-btn>
              </template>
              <TooltipDescription title="Edit Row" desc_2=""></TooltipDescription>
          </v-tooltip>
          <v-tooltip top v-if="rowDuplicable && isCoreRow(item)">
              <template v-slot:activator="{ on }">
                  <v-btn icon elevation="0" v-on="on" @click="duplicateOnClick(item)" :disabled="actions_locked">
                      <v-icon small color="black">mdi-content-copy</v-icon>
                  </v-btn>
              </template>
              <TooltipDescription title="Duplicate Row" desc_2=""></TooltipDescription>
          </v-tooltip>
          <v-tooltip top v-if="rowDeletable && isCoreRow(item)">
              <template v-slot:activator="{ on }">
                  <v-btn icon elevation="0" v-on="on" @click="deleteOnClick(item)" :disabled="actions_locked">
                      <v-icon small color="black">mdi-delete</v-icon>
                  </v-btn>
              </template>
              <TooltipDescription title="Delete Row" desc_2=""></TooltipDescription>
          </v-tooltip>
        </div>
      </div>
      <div
        v-else
        :key="numCol.value"
        :style="{
          backgroundColor: update_color(item),
          maxWidth: resolvedStringCellMaxWidth
        }"
        class="stringCellFlighting ma-0 pa-1 px-2"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="2" class="ma-0 pa-0 pr-2 d-flex justify-center align-center" v-if="color && numCol.value == selectedLevels[0]">
            <div
              :style="{
                backgroundColor: getColorIndicatorColor(item, index),
                width: '12px',
                height: '12px',
                borderRadius: '50%'
              }"
            ></div>
          </v-col>
          <v-col :cols="(color && numCol.value == selectedLevels[0]) ? 10:12" class="ma-0 pa-0">
            <div
              class="string-cell-content"
              :class="{ editableStringCell: canEditCell(item, numCol.value) }"
              @click="handleStringCellClick($event, item, numCol.value)"
            >
              <v-text-field
                v-if="isEditingCell(item, numCol.value)"
                :value="inlineEditDraftValue"
                :error-messages="inlineEditError ? [inlineEditError] : []"
                class="inlineStringEditField"
                dense
                hide-details="auto"
                outlined
                single-line
                type="text"
                autofocus
                @blur="handleEditBlur(item, numCol.value)"
                @click.stop
                @input="updateEditValue"
                @keydown.enter.stop.prevent="handleEditEnter(item, numCol.value)"
                @keydown.esc.stop.prevent="handleEditEscape"
              ></v-text-field>
              <v-tooltip
                v-else
                top
                :disabled="getCellDisplayValue(item, numCol.value) === ''"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    class="stringCellContent"
                    v-on="on"
                  >
                    {{ getCellDisplayValue(item, numCol.value) }}
                  </span>
                </template>
                <span class="stringCellTooltipText">
                  {{ getCellDisplayValue(item, numCol.value) }}
                </span>
              </v-tooltip>
            </div>
           </v-col>
        </v-row>
      </div>
    </template>
  </v-data-table>
  <div v-if="addNewRowEnabled" class="add-new-row-container">
    <v-btn class="add-new-row-btn" outlined text @click="$emit('add_new_row')">
      <v-icon left>mdi-plus</v-icon> {{ addNewRowText }}
    </v-btn>
  </div>
  </div>
</template>

<script>
import TooltipDescription from "@/components/Utility/TooltipDescription.vue";

export default {
  name: "DataLevelTable",
  props: [
    "tableHeader",
    "tableData",
    "selectedLevels",
    "tableRowClickFunction",

    "numericColumns",
    "decimalColumns",
    "currencyColumns",
    "percentageColumns",
    "reversedPercentageColorColumns",

    'rowEditable',
    'rowDeletable',
    'rowDuplicable',
    "filterable",
    "filter_all_vals",
    "data_filters",
    "filterBaseData",
    "inlineEditConfig",
    "stringCellMaxWidth",
    "super_short",
    "short",
    "mid",
    "actions_locked",
    "color",
    "colorMap",
    "not_sorted",
    "addNewRowEnabled",
    "addNewRowText",
    "showSelectedLevelValues",
    "reducedRowHeight",
    "locationIconStyles",
    "tableHeight"
  ],
  components: {
    TooltipDescription,
  },
  created() {
    this.initializeFilterState();
  },
  mounted() {
    this.$nextTick(() => {
      this.refreshTableLayout();
    });
    window.addEventListener("resize", this.refreshTableLayout);
  },
  updated() {
    this.$nextTick(() => {
      this.refreshTableLayout();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.refreshTableLayout);
  },
  data: () => ({
    dialogDelete: false,
    hasHorizontalOverflow: false,
    filter_vals: {},
    filter_search_vals: {},
    filter_base: {},
    filter_menus: {},
    skipNextEditBlur: false,
    colorList: [
      '#FF4C4C', // Red
      '#4C9BFF', // Blue
      '#4CD7D0', // Teal
      '#F4B400', // Yellow
      '#A07BFF', // Purple
      '#00C49F', // Green
      '#FFBB28', // Orange
      '#FF7F50', // Coral
      '#1E90FF', // Dodger Blue
      '#20B2AA', // Light Sea Green
      '#FFD700', // Gold
      '#BA68C8', // Light Purple
      '#2E8B57', // Sea Green
      '#FFA500', // Deep Orange
      '#E57373', // Light Red
      '#64B5F6', // Light Blue
      '#81C784', // Light Green
      '#FF8A65', // Salmon
      '#9575CD', // Medium Purple
      '#4DB6AC', // Aqua Green
      '#F06292', // Pink
      '#7986CB', // Indigo
      '#AED581', // Light Lime
      '#FFD54F', // Light Amber
      '#90A4AE'  // Cool Gray
    ]
  }),
  watch: {
    tableData(){
      this.initializeFilterState();
    },
    dialog (val) {
      val || this.close()
    },
    dialogDelete (val) {
      val || this.closeDelete()
    },
    filterable(val) {
      if(!val) {
        this.initializeFilterState();
        this.$emit('filter_data', {});
      }
    }
  },
  computed:{
    tableMaxHeight() {
      if (typeof this.tableHeight === "string" && this.tableHeight.trim() !== "") {
        return this.tableHeight;
      }

      const super_short_enabled = this.super_short === true || this.super_short === "" || this.super_short === "true";
      const short_enabled = this.short === true || this.short === "" || this.short === "true";
      const mid_enabled = this.mid === true || this.mid === "" || this.mid === "true";

      if (super_short_enabled){
        return "200px";
      }
      if (short_enabled) {
        return "300px";
      }

      if (mid_enabled) {
        return "calc(100vh - 475px)";
      }

      return "calc(100vh - 400px)";
    },
    reducedRowHeightEnabled() {
      return this.reducedRowHeight === true || this.reducedRowHeight === "" || this.reducedRowHeight === "true";
    },
    computedHeaders() {
      if (!this.color) return this.tableHeader;
      return [
        { text: '', value: 'colorDot', sortable: false }, // new column
        ...this.tableHeader
      ];
    },
    resolvedStringCellMaxWidth() {
      if (typeof this.stringCellMaxWidth === "number") {
        return `${this.stringCellMaxWidth}px`;
      }

      if (
        typeof this.stringCellMaxWidth === "string" &&
        this.stringCellMaxWidth.trim() !== ""
      ) {
        return this.stringCellMaxWidth;
      }

      return "240px";
    },
    resolvedInlineEditConfig() {
      return this.inlineEditConfig || {};
    },
    inlineEditDraftValue() {
      return this.resolvedInlineEditConfig.draftValue;
    },
    inlineEditError() {
      return this.resolvedInlineEditConfig.error;
    },
  },
  methods: {
    initializeFilterState() {
      const selectedLevels = Array.isArray(this.selectedLevels) ? this.selectedLevels : [];
      const dataFilters = this.data_filters || {};

      this.filter_vals = {};
      this.filter_base = {};
      this.filter_menus = {};

      selectedLevels.forEach((lvl) => {
        const availableVals = this.get_filter_vals(lvl);
        const selectedVals = Array.isArray(dataFilters[lvl]) && dataFilters[lvl].length > 0
          ? dataFilters[lvl]
          : availableVals;

        this.$set(this.filter_vals, lvl, selectedVals);
        this.$set(this.filter_base, lvl, availableVals);
        this.$set(this.filter_menus, lvl, false);
      });
    },
    refreshTableLayout() {
      this.applyStickyColumns();
      this.updateHorizontalOverflow();
    },
    updateHorizontalOverflow() {
      if (!this.$el) {
        return;
      }

      const wrapper = this.$el.querySelector(".v-data-table__wrapper");
      const table = wrapper ? wrapper.querySelector("table") : null;

      if (!wrapper || !table) {
        this.hasHorizontalOverflow = false;
        return;
      }

      const wrapperWidth = Math.ceil(wrapper.getBoundingClientRect().width);
      const tableWidth = Math.ceil(table.getBoundingClientRect().width);
      const scrollWidth = Math.ceil(wrapper.scrollWidth);
      const contentWidth = Math.max(tableWidth, scrollWidth);
      const hasHorizontalOverflow = contentWidth > wrapperWidth + 2;

      if (this.hasHorizontalOverflow !== hasHorizontalOverflow) {
        this.hasHorizontalOverflow = hasHorizontalOverflow;
      }
    },
    formatNumericCellValue(value, col) {
      if (value == null || value === "") {
        return "n/a";
      }

      if (this.currencyColumns.includes(col)) {
        return Number(value).toLocaleString("en-CA", {
          style: "currency",
          currency: "CAD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      if (this.decimalColumns.includes(col)) {
        return Number(value).toLocaleString("en-CA", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      if (this.percentageColumns.includes(col)) {
        return Number(value).toLocaleString("en-CA", {
          style: "percent",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      return Number(value).toLocaleString("en-CA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    hasLocationCoordinates(item) {
      const latitude = Number(item && item.latitude);
      const longitude = Number(item && item.longitude);
      return Number.isFinite(latitude) && Number.isFinite(longitude);
    },
    getLocationIconStyle(item) {
      const styles = this.locationIconStyles || {};
      const styleKey = item && (item.product || item.location_icon_style);
      return styles[styleKey] || { color: "#1F2933", shape: "circle" };
    },
    getLocationMarkerStyle(item) {
      const style = this.getLocationIconStyle(item);

      return {
        background: style.color,
        color: style.color,
      };
    },
    isEditableColumn(col) {
      return (
        Array.isArray(this.resolvedInlineEditConfig.columns) &&
        this.resolvedInlineEditConfig.columns.includes(col)
      );
    },
    isCoreRow(item) {
      if (!item) {
        return false;
      }

      if (!Array.isArray(this.selectedLevels) || this.selectedLevels.length === 0) {
        return true;
      }

      return this.selectedLevels.every((level) => {
        return item[level] != null && item[level] !== "";
      });
    },
    canEditCell(item, col) {
      return this.isEditableColumn(col) && this.isCoreRow(item);
    },
    getEditInputMode(col) {
      if (
        this.decimalColumns.includes(col) ||
        this.currencyColumns.includes(col) ||
        this.percentageColumns.includes(col)
      ) {
        return "decimal";
      }

      return "numeric";
    },
    getEditStep(col) {
      if (
        this.decimalColumns.includes(col) ||
        this.currencyColumns.includes(col) ||
        this.percentageColumns.includes(col)
      ) {
        return "0.01";
      }

      return "1";
    },
    getEditableRowKey(item) {
      const keyField = this.resolvedInlineEditConfig.keyField;

      if (!keyField || !item) {
        return null;
      }

      return item[keyField];
    },
    isEditingCell(item, col) {
      const activeCell = this.resolvedInlineEditConfig.activeCell;

      if (!activeCell || activeCell.column !== col) {
        return false;
      }

      return activeCell.rowKey === this.getEditableRowKey(item);
    },
    startEditCell(item, col) {
      if (!this.canEditCell(item, col) || this.actions_locked) {
        return;
      }

      this.$emit("start_edit_cell", {
        row: item,
        column: col,
        rowKey: this.getEditableRowKey(item),
        value: item[col],
      });
    },
    handleStringCellClick(event, item, col) {
      if (!this.canEditCell(item, col) || this.actions_locked) {
        return;
      }

      event.stopPropagation();
      this.startEditCell(item, col);
    },
    updateEditValue(value) {
      this.$emit("update_edit_value", value);
    },
    handleEditBlur(item, col) {
      if (this.skipNextEditBlur) {
        this.skipNextEditBlur = false;
        return;
      }

      this.saveEditCell(item, col);
    },
    handleEditEnter(item, col) {
      this.skipNextEditBlur = true;
      this.saveEditCell(item, col);
    },
    handleEditEscape() {
      this.skipNextEditBlur = true;
      this.cancelEditCell();
    },
    saveEditCell(item, col) {
      if (!this.isEditingCell(item, col)) {
        return;
      }

      this.$emit("save_edit_cell", {
        row: item,
        column: col,
        rowKey: this.getEditableRowKey(item),
        value: this.inlineEditDraftValue,
      });
    },
    cancelEditCell() {
      this.$emit("cancel_edit_cell");
    },
    clearStickyColumns() {
      if (!this.$el) {
        return;
      }

      this.$el.querySelectorAll("th, td").forEach((cell) => {
        cell.style.position = "";
        cell.style.left = "";
        cell.style.width = "";
        cell.style.minWidth = "";
        cell.style.maxWidth = "";
        cell.style.zIndex = "";
        cell.style.backgroundColor = "";
        cell.style.backgroundImage = "";
        cell.style.boxShadow = "";
        cell.style.borderBottom = "";
      });
    },
    applyStickyColumns() {
      if (!this.$el) {
        return;
      }

      const stickyColumnCount = Array.isArray(this.selectedLevels)
        ? this.selectedLevels.length
        : 0;

      this.clearStickyColumns();

      if (stickyColumnCount === 0) {
        return;
      }

      const table = this.$el.querySelector(".v-data-table__wrapper table");
      const headerCells = table ? table.querySelectorAll("thead tr th") : [];

      if (!table || headerCells.length === 0) {
        return;
      }

      this.applyBodyCellBackgrounds(table);

      let leftOffset = 0;
      const columnWidths = Array.from(headerCells).map((headerCell) => {
        return Math.round(headerCell.getBoundingClientRect().width);
      });

      for (
        let columnIndex = 0;
        columnIndex < stickyColumnCount && columnIndex < headerCells.length;
        columnIndex += 1
      ) {
        const headerCell = headerCells[columnIndex];
        const columnWidth = columnWidths[columnIndex];
        const stickyLeftOffset = Math.max(0, leftOffset - columnIndex);

        this.setStickyCellStyles(headerCell, stickyLeftOffset, columnWidth, true);

        table
          .querySelectorAll(`tbody tr td:nth-child(${columnIndex + 1})`)
          .forEach((cell) => {
            this.setStickyCellStyles(cell, stickyLeftOffset, columnWidth, false);
          });

        leftOffset += columnWidth;
      }
    },
    applyBodyCellBackgrounds(table) {
      table.querySelectorAll("tbody tr td").forEach((cell) => {
        const backgroundColor = this.getCellBackgroundColor(cell, false);
        cell.style.backgroundColor = backgroundColor;
      });
    },
    getCellBackgroundColor(cell, isHeader) {
      if (isHeader) {
        return "#FFFFFF";
      }

      const cellContent = cell.firstElementChild;
      const contentStyles = cellContent ? window.getComputedStyle(cellContent) : null;
      const fallbackBackground = window.getComputedStyle(cell).backgroundColor;
      const backgroundColor = (
        contentStyles &&
        contentStyles.backgroundColor !== "rgba(0, 0, 0, 0)"
      )
        ? contentStyles.backgroundColor
        : fallbackBackground;

      return backgroundColor === "rgba(0, 0, 0, 0)" ? "#FFFFFF" : backgroundColor;
    },
    setStickyCellStyles(cell, leftOffset, columnWidth, isHeader) {
      const backgroundColor = this.getCellBackgroundColor(cell, isHeader);

      cell.style.position = "sticky";
      cell.style.left = `${leftOffset}px`;
      cell.style.width = `${columnWidth + 1}px`;
      cell.style.minWidth = `${columnWidth + 1}px`;
      cell.style.maxWidth = `${columnWidth + 1}px`;
      cell.style.zIndex = isHeader ? "6" : "4";
      cell.style.backgroundColor = backgroundColor;
      cell.style.backgroundImage = "none";
      cell.style.boxShadow = `2px 0 0 ${backgroundColor}`;
      cell.style.borderBottom = "none";
    },
    arraysEqual(a, b) {
      if (a === b) return true;
      if (a == null || b == null) return false;
      if (a.length !== b.length) return false;

      let a_sort = [...a].sort()
      let b_sort = [...b].sort()

      for (var i = 0; i < a.length; ++i) {
        if (a_sort[i] !== b_sort[i]) return false;
      }
      return true;
    },
    save_enabled: function(col){
      let equal = this.arraysEqual(this.filter_vals[col], this.filter_base[col]);
      return equal;
    },
    get_filter_vals: function(col){
      function onlyUnique(value, index, array) {
        return array.indexOf(value) === index;
      }
      let filter_vals = []
      const filterRows = Array.isArray(this.filterBaseData) ? this.filterBaseData : this.tableData;
      for(let row of filterRows){
        if(row[col] != null){
          filter_vals.push(row[col]);
        }
      }
      filter_vals = filter_vals.filter(onlyUnique)
      return filter_vals
    },
    cancel_filter: function(col){
      this.filter_vals[col] = this.filter_base[col];
    },
    save_filter: function(){
      let save_filters = {}
      for(let lvl of Object.keys(this.filter_vals)){
        if(this.filter_all_vals[lvl].length == this.filter_vals[lvl].length){
          // save_filters[lvl] = [];
          continue;
        }
        else{
          save_filters[lvl] = this.filter_vals[lvl];
        }
      }
      this.$emit('filter_data', save_filters);
    },
    update_color: function (item) {
      // let grey_notches = ["#FFFFFF","#cccccc","#808080"];
      let grey_notches = [
        "#FFFFFF",
        "#F0F0F0",
        "#DCDCDC",
        "#D0D0D0",
        "#BEBEBE",
        "#A9A9A9",
      ];
      let null_count = 0;
      for (let level of this.selectedLevels) {
        if (item[level] == null) {
          null_count++;
        }
      }
      return grey_notches[null_count];
    },
    update_text_color: function (item, numCol){
      const value = item[numCol.value];
      const isChangeColumn = numCol.value.includes('change');

      if (!isChangeColumn || !value) {
        return 'black';
      }

      const reversedColumns = Array.isArray(this.reversedPercentageColorColumns)
        ? this.reversedPercentageColorColumns
        : [];
      const isReversed = reversedColumns.includes(numCol.value);

      if (value >= 0) {
        return isReversed ? 'red' : 'green';
      }

      return isReversed ? 'green' : 'red';
    },
    getColorIndicatorColor: function (item, index) {
      const colorLevel = this.selectedLevels && this.selectedLevels.length > 0
        ? this.selectedLevels[0]
        : null;
      const colorKey = colorLevel ? item[colorLevel] : null;

      if (
        this.colorMap &&
        colorKey != null &&
        Object.prototype.hasOwnProperty.call(this.colorMap, colorKey)
      ) {
        return this.colorMap[colorKey];
      }

      return this.colorList[index % this.colorList.length];
    },
    getCellDisplayValue: function (item, col) {
      const value = this.level_val(item, col);
      return value == null ? "" : String(value);
    },
    level_val: function (item, col) {
      if (this.showSelectedLevelValues) {
        return item[col];
      }

      let idx = this.selectedLevels.indexOf(col);

      if (idx < 0) {
        return item[col];
      }

      let hide = false;
      for (let i = this.selectedLevels.length - 1; i > idx; i--) {
        if (item[this.selectedLevels[i]] != null) {
          hide = true;
        }
      }
      if (hide) {
        return "";
      } else {
        return item[col];
      }
    },
    deleteOnClick: function(item) {
      if (!this.isCoreRow(item)) {
        return;
      }

      this.itemToDelete = item
      this.dialogDelete=true;
    },
    closeDelete: function() {
      this.dialogDelete = false
    },
    deleteItemConfirm:function() {
      this.$emit('deleteItem', this.itemToDelete);
      this.dialogDelete = false
    },
    editOnClick: function(item) {
      if (!this.isCoreRow(item)) {
        return;
      }

      this.$emit('editItem', item);
    },
    duplicateOnClick: function(item) {
      if (!this.isCoreRow(item)) {
        return;
      }

      this.$emit('duplicateItem', item);
    },
    sortDataTableLevels(items, sortBy, sortDesc, locale) {
        if(sortBy.length == 0){
          sortBy = [...this.selectedLevels];
          sortDesc = [];
          for(let i = 0; i < this.selectedLevels.length; i++){
            sortDesc.push(false)
          }
          if(this.not_sorted){
            return items;
          }
        }
        // Define a comparator function
        const comparator = (a, b, key, descending) => {
          let valueA = a[key];
          let valueB = b[key];
          let result = 0;
          
          let isSelectedLevel = this.selectedLevels.includes(key);

          if (valueA == null && valueB == null) {
              result = 0;
          } else if (valueA == null) {
              result = isSelectedLevel ? -1 : 1;
              return result;
          } else if (valueB == null) {
              result = isSelectedLevel ? 1 : -1;
              return result;
          } else {
            if (typeof valueA === 'string' && typeof valueB === 'string') {
              result = valueA.localeCompare(valueB, locale, { numeric: true });
            } else if (typeof valueA === 'number' && typeof valueB === 'number') {
              result = valueA - valueB;
            } else if (valueA instanceof Date && valueB instanceof Date) {
              result = valueA - valueB;
            } else {
              result = String(valueA).localeCompare(String(valueB), locale, { numeric: true });
            }
          }
          // Adjust for descending order
          return descending ? -result : result;
        };
        
        if(this.selectedLevels.length > 1 && !this.showSelectedLevelValues){
          const groupItems = (items, selectedLevels) => {
              // Helper function to create the hierarchical structure
              const createHierarchy = (items, levels) => {
                  if (levels.length === 0) {
                      return [];
                  }

                  const level = levels[0];
                  const grouped = items.reduce((acc, item) => {
                      const key = item[level];
                      if (key === undefined) return acc;

                      if (!acc[key]) {
                          acc[key] = [];
                      }
                      acc[key].push(item);
                      return acc;
                  }, {});

                  return Object.keys(grouped).map(key => {
                      const children = createHierarchy(grouped[key], levels.slice(1));
                      return {
                          parent: { ...grouped[key][0], [level]: key },
                          children
                      };
                  });
              };

              return createHierarchy(items, selectedLevels);
          };
          // Group and sort the items
          let groupedItems = groupItems(items, this.selectedLevels);

          // Function to sort the groups and their children
          const sortGroups = (groupedItems, sortBy, sortDesc) => {
              groupedItems.sort((a, b) => {
                  for (let i = 0; i < sortBy.length; i++) {
                      const key = sortBy[i];
                      const descending = sortDesc[i];

                      const result = comparator(a.parent, b.parent, key, descending);
                      if (result !== 0) return result;
                  }
                  return 0;
              });

              groupedItems.forEach(group => {
                  if (group.children && group.children.length > 0) {
                      // Recursively sort subgroups
                      sortGroups(group.children, sortBy, sortDesc);
                  }
              });
          };

          sortGroups(groupedItems, sortBy, sortDesc);

          // Flatten the groupedItems array recursively to get the final sorted list
          const flattenGroups = (groupedItems) => {
              return groupedItems.reduce((acc, group) => {
                  if (group.parent) acc.push(group.parent);
                  return acc.concat(flattenGroups(group.children));
              }, []);
          };

          const sortedItems = flattenGroups(groupedItems);

          return sortedItems;
        }
        else{
          // Sort the items array
          items.sort((a, b) => {
              for (let i = 0; i < sortBy.length; i++) {
                  const key = sortBy[i];
                  const descending = sortDesc[i];

                  const result = comparator(a, b, key, descending);
                  if (result !== 0) return result;
              }
              return 0; // If all comparisons are equal
          });
        }

        return items;
    },
    getDataLevelSelectAllIcon(col){
      if(this.filter_vals[col].length == this.filter_all_vals[col].length){
        return 'mdi-checkbox-marked'
      }
      else if(this.filter_vals[col].length == 0){
        return 'mdi-checkbox-blank-outline'
      }
      else{
        return 'mdi-minus-box'
      }
    },
    toggleDataLevelAllFilter: function(col) {
      if(this.filter_vals[col].length == this.filter_all_vals[col].length){
        this.filter_vals[col] = [];
      }
      else{
        this.filter_vals[col] = this.filter_all_vals[col];
      }
    },
    filter_all_vals_searched: function(col) {
      let filter_all_vals_searched;
      let search_val = this.filter_search_vals[col] ? this.filter_search_vals[col].toLowerCase() : '';
      if(search_val == ''){
        filter_all_vals_searched = this.filter_all_vals[col];
      }
      else{
        filter_all_vals_searched  = this.filter_all_vals[col].filter(item => item.toLowerCase().includes(search_val));
      }
      return filter_all_vals_searched
    }
  },
};
</script>

<style>

.numeric-cell-content {
  width: 80%;
  margin: 0 auto;
}

.inlineNumericEditField {
  margin-top: 2px !important;
}

.inlineStringEditField {
  margin-top: 2px !important;
}

.editableNumericCell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  cursor: text;
}

.editableStringCell {
  cursor: text;
  min-height: 40px;
}

.dataLevelTable .v-data-table__wrapper {
  max-height: var(--data-level-table-max-height);
  overflow-y: auto;
  overflow-x: hidden;
}

.dataLevelTable.dataLevelTable--has-horizontal-overflow .v-data-table__wrapper {
  overflow-x: auto;
}

.dataLevelTable .v-data-table > .v-data-table__wrapper > table {
  border-collapse: separate;
  border-spacing: 0;
  cursor: pointer;
}

.dataLevelTable .v-data-table-header > tr > th {
  min-width: 150px;
  background: #ffffff !important;
}

.dataLevelTable .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 0 !important;
  border: 0 !important;
}

.dataLevelTable .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background: transparent !important;
}

.headerCellFlighting {
  float: inline-start;
  max-width: 80%;
  font-family: "Poppins" !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

.headerCellContent {
  float: right;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.editableHeaderIndicator {
  margin-left: 4px;
  opacity: 0.8;
  pointer-events: none;
}

.headerStringCellFligthing {
  float: inline-start;
  max-width: 150% !important;
  font-family: "Poppins" !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

.numericCellFlighting {
  box-sizing: border-box;
  text-align: center;
  line-height: 48px;
  width: 100%;
  height: 100%;
  /* max-width: 250px; */
  font-family: "Poppins" !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}
.naCellFlighting {
  text-align: center;
  line-height: 48px;
  /* width: 100%; */
  height: 100%;
}
.stringCellFlighting {
  box-sizing: border-box;
  text-align: left;
  line-height: 48px;
  width: 100%;
  height: 100%;
  font-family: "Poppins" !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}
.dataLevelTable--reduced-row-height .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  height: 36px !important;
}
.dataLevelTable--reduced-row-height .v-data-table-header > tr > th {
  height: 38px !important;
}
.dataLevelTable--reduced-row-height .headerCellFlighting,
.dataLevelTable--reduced-row-height .headerStringCellFligthing {
  line-height: 32px;
}
.dataLevelTable--reduced-row-height .numericCellFlighting,
.dataLevelTable--reduced-row-height .naCellFlighting,
.dataLevelTable--reduced-row-height .stringCellFlighting {
  line-height: 34px;
}
.locationCellFlighting {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}
.locationMarker {
  border: 2px solid #111827;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.82), 0 2px 6px rgba(0, 0, 0, 0.35);
  display: block;
  height: 15px;
  width: 15px;
}
.locationMarker--circle {
  border-radius: 50%;
}
.locationMarker--diamond {
  transform: rotate(45deg);
}
.locationMarker--pin {
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
}
.locationMarker--square {
  border-radius: 2px;
}
.locationMarker--triangle {
  background: transparent !important;
  border-bottom: 16px solid currentColor;
  border-left: 8px solid transparent !important;
  border-right: 8px solid transparent !important;
  border-top: 0 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
  height: 0;
  width: 0;
}
.dataLevelTable--reduced-row-height .editableNumericCell,
.dataLevelTable--reduced-row-height .editableStringCell {
  min-height: 32px;
}
.dataLevelTable--reduced-row-height .actionCell .v-btn {
  height: 30px !important;
  width: 30px !important;
}
.stringCellContent {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stringCellTooltipText {
  display: block;
  max-width: 320px;
  white-space: normal;
  word-break: break-word;
}
.actionCell {
  box-sizing: border-box;
  display: flex;
  height: 100%;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  
  .actionCellContent {
    margin: auto 0;
    cursor: pointer;
  }
}

.filter_card{
  width: 400px;
}
.add-new-row-container {
  display: flex;
  justify-content: center;
  padding: 8px 16px 24px;
}
.add-new-row-btn {
  cursor: pointer;
}

.dialog_title {
  font-family: "Poppins" !important;
  font-weight: 600 !important;
  font-size: 1.2em !important;
}
.action_text {
  font-family: "Poppins" !important;
  font-weight: 400 !important;
  font-size: 0.9em !important;
}
.dialog_content {
  font-family: "Poppins" !important;
  font-weight: 400;
  font-size: 1em;
}
</style>
